import { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/system';
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import api from 'api';
import CustomDropdown from 'components/dropdown/CustomDropdown';
import { gridSpacing } from 'store/constant';
import FilledBtn from 'components/buttons/FilledBtn';
import { adminAnnouncementsQueryKey, getAdminAnnouncements } from 'api/adminAnnouncements';

const audienceOptions = [
  { value: 'all', label: 'All User' },
  { value: 'fundManager', label: 'All Fund Manager' },
  { value: 'investor', label: 'All Investor' },
  { value: 'select_fund_managers', label: 'Select Fund Manager' },
  { value: 'select_investors', label: 'Select Investor' }
];

const notificationTypeOptions = [
  { value: 'comment', label: 'Comment' },
  { value: 'deal', label: 'Deal' },
  { value: 'chat', label: 'Chat' },
  { value: 'system', label: 'System' },
  { value: 'approval', label: 'Approval' },
  { value: 'capital_call', label: 'Capital Call' },
  { value: 'fund_update', label: 'Fund Update' },
  { value: 'performance_report', label: 'Performance Report' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'announcement', label: 'Announcement' }
];

const toIsoStartOfDay = (dateStr) => {
  if (!dateStr) return undefined;
  const d = new Date(`${dateStr}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

const toIsoEndOfDay = (dateStr) => {
  if (!dateStr) return undefined;
  const d = new Date(`${dateStr}T23:59:59.999`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

const messageSnippet = (message, max = 96) => {
  if (message == null || message === '') return '—';
  const s = String(message).replace(/\s+/g, ' ').trim();
  if (s.length <= max) return s;
  return `${s.slice(0, max)}…`;
};

const Announcements = () => {
  const queryClient = useQueryClient();

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [type, setType] = useState('announcement');
  const [audience, setAudience] = useState('all');
  const [submitting, setSubmitting] = useState(false);

  const [fundManagers, setFundManagers] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  const [listPage, setListPage] = useState(0);
  const [listLimit, setListLimit] = useState(20);
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');
  const [adminSentOnly, setAdminSentOnly] = useState(true);

  const listQueryParams = useMemo(() => {
    const page = listPage + 1;
    const limit = listLimit;
    const from = toIsoStartOfDay(filterFromDate);
    const to = toIsoEndOfDay(filterToDate);
    const sentBy = adminSentOnly ? 'admin' : undefined;
    return { page, limit, from, to, sentBy };
  }, [listPage, listLimit, filterFromDate, filterToDate, adminSentOnly]);

  useEffect(() => {
    setListPage(0);
  }, [filterFromDate, filterToDate, adminSentOnly]);

  const announcementsQuery = useQuery({
    queryKey: adminAnnouncementsQueryKey(listQueryParams),
    queryFn: () => getAdminAnnouncements(listQueryParams),
    placeholderData: keepPreviousData
  });

  const {
    data: announcementsData,
    isLoading: announcementsLoading,
    error: announcementsError,
    refetch: refetchAnnouncements
  } = announcementsQuery;

  const rows = announcementsData?.data ?? [];
  const total = typeof announcementsData?.total === 'number' ? announcementsData.total : 0;

  const isSelectFundManagers = audience === 'select_fund_managers';
  const isSelectInvestors = audience === 'select_investors';

  const loadFundManagers = async () => {
    try {
      setLoadingList(true);
      const response = await api.get(`/users/fund-managers?page=1&limit=1000&isAccepted=true&searchTerm=&nameSort=&emailSort=`);
      if (response.status === 200) {
        setFundManagers(response.data.data || []);
      }
    } catch (error) {
      console.log('Error fetching fund managers:', error);
      toast.error('Failed to load fund managers');
    } finally {
      setLoadingList(false);
    }
  };

  const loadInvestors = async () => {
    try {
      setLoadingList(true);
      const response = await api.get(`/users/investors?page=1&limit=1000&isAccepted=true&searchTerm=&nameSort=&emailSort=`);
      if (response.status === 200) {
        setInvestors(response.data.data || []);
      }
    } catch (error) {
      console.log('Error fetching investors:', error);
      toast.error('Failed to load investors');
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    setSelectedUserIds([]);
    if (isSelectFundManagers) {
      loadFundManagers();
    } else if (isSelectInvestors) {
      loadInvestors();
    }
  }, [audience]);

  const validate = () => {
    if (typeof title !== 'string' || title.trim().length < 3) {
      toast.error('Title must be at least 3 characters');
      return false;
    }
    if (typeof message !== 'string' || message.trim().length < 5) {
      toast.error('Message must be at least 5 characters');
      return false;
    }
    const validType = notificationTypeOptions.some((opt) => opt.value === type);
    if (!validType) {
      toast.error('Please select a valid notification type');
      return false;
    }
    if ((isSelectFundManagers || isSelectInvestors) && selectedUserIds.length === 0) {
      toast.error('Please select at least one recipient');
      return false;
    }
    return true;
  };

  const handleToggleUser = (userId) => {
    setSelectedUserIds((prev) => {
      if (prev.includes(userId)) return prev.filter((id) => id !== userId);
      return [...prev, userId];
    });
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      let apibody = {
        title: title.trim(),
        message: message.trim(),
        type
      };
      if (audience == 'investor' || audience == 'fundManager') {
        apibody = {
          ...apibody,
          userRole: audience
        };
      } else if (audience == 'select_fund_managers' || audience == 'select_investors') {
        apibody = {
          ...apibody,
          userIds: selectedUserIds
        };
      }
      const response = await api.post('/notifications/admin-send', apibody);
      if (response.status == 201) {
        if (response.data.sent > 0) {
          toast.success(response.data.message || 'Notification sent successfully ');
          setTitle('');
          setMessage('');
          setType('announcement');
          setAudience('all');
          setSelectedUserIds([]);
          queryClient.invalidateQueries({ queryKey: ['admin', 'announcements'] });
        } else {
          toast.error(response.data.message || 'Failed to send notification ');
        }
      }
    } catch (err) {
      console.error('Error sending notification:', err);
      toast.error(err?.response?.data?.message || 'Failed to send notification');
    } finally {
      setSubmitting(false);
    }
  };

  const listErrorMessage =
    announcementsError?.response?.status === 403
      ? 'You do not have access to this list (admin or superAdmin only).'
      : announcementsError?.response?.data?.message || announcementsError?.message;

  return (
    <>
      <Grid container spacing={gridSpacing}>
        <Grid item xs={12}>
          <Box
            sx={{
              background: 'white',
              padding: '24px',
              borderRadius: '15px',
              boxShadow:
                '7px 7px 10px rgba(0, 0, 0, 0.1), inset -5px -5px 5px rgba(255, 255, 255, 0.9)'
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography style={{ fontSize: '20px', lineHeight: '30px', fontWeight: '500' }}>Announcements</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth size="small" />
                <TextField
                  label="Message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  fullWidth
                  size="small"
                  multiline
                  minRows={4}
                />
                <CustomDropdown
                  label="Notification Type"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  placeholder="Select Type"
                  options={notificationTypeOptions}
                  minWidth={'100%'}
                />
                <CustomDropdown
                  label="Audience"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  placeholder="Select Audience"
                  options={audienceOptions}
                  minWidth={'100%'}
                />
              </Box>

              {(isSelectFundManagers || isSelectInvestors) && (
                <Box sx={{ mt: 1 }}>
                  <Typography style={{ fontSize: '16px', lineHeight: '24px', fontWeight: '600' }}>
                    {isSelectFundManagers ? 'Select Fund Managers' : 'Select Investors'}
                  </Typography>
                  {loadingList ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <CircularProgress size={18} />
                      <Typography>Loading...</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px', p: 1, mt: 1 }}>
                      <FormGroup>
                        {(isSelectFundManagers ? fundManagers : investors).map((u) => (
                          <FormControlLabel
                            key={u._id}
                            control={<Checkbox checked={selectedUserIds.includes(u._id)} onChange={() => handleToggleUser(u._id)} />}
                            label={`${u?.name || ''}  ${u?.email ? `- ${u.email}` : ''}`}
                          />
                        ))}
                      </FormGroup>
                    </Box>
                  )}
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <FilledBtn
                  btnText={submitting ? 'Sending...' : 'Send'}
                  bgColor={'#430074'}
                  fontSize={'12px'}
                  onClick={handleSubmit}
                  disabled={submitting}
                />
              </Box>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              background: 'white',
              padding: '24px',
              borderRadius: '15px',
              boxShadow: '7px 7px 10px rgba(0, 0, 0, 0.1), inset -5px -5px 5px rgba(255, 255, 255, 0.9)'
            }}
          >
            <Typography sx={{ fontSize: '20px', fontWeight: 500, mb: 2 }}>Announcement notifications (admin)</Typography>
            {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              One row per recipient. <code>GET /notifications/admin/announcements</code>
            </Typography> */}

            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="From (created)"
                  InputLabelProps={{ shrink: true }}
                  value={filterFromDate}
                  onChange={(e) => setFilterFromDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="To (created)"
                  InputLabelProps={{ shrink: true }}
                  value={filterToDate}
                  onChange={(e) => setFilterToDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={adminSentOnly}
                      onChange={(e) => {
                        setAdminSentOnly(e.target.checked);
                      }}
                    />
                  }
                  label='Only admin sends (sentBy = "admin")'
                />
              </Grid>
              <Grid item xs={12} md="auto">
                <Button
                  variant="outlined"
                  onClick={() => refetchAnnouncements()}
                  sx={{ borderColor: '#420074', color: '#420074' }}
                  disabled={announcementsLoading}
                >
                  Refresh
                </Button>
              </Grid>
            </Grid>

            {announcementsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {listErrorMessage}
              </Alert>
            )}

            <TableContainer component={Paper} variant="outlined">
              {announcementsLoading && !announcementsData ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress sx={{ color: '#430074' }} />
                </Box>
              ) : (
                <Table size="small">
                  <TableHead sx={{ backgroundColor: '#EAEFEC' }}>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Message</TableCell>
                      <TableCell>Recipient</TableCell>
                      <TableCell>Read</TableCell>
                      <TableCell>Sent by</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="text.secondary" sx={{ py: 3 }}>
                            No rows
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row) => {
                        const u = row.userId;
                        const recipient = u && (u.name || u.email) ? [u.name, u.email, u.role].filter(Boolean).join(' · ') : u?._id || '—';
                        return (
                          <TableRow key={row._id} hover>
                            <TableCell sx={{ maxWidth: 180 }}>{row.title || '—'}</TableCell>
                            <TableCell sx={{ maxWidth: 280 }}>{messageSnippet(row.message)}</TableCell>
                            <TableCell sx={{ maxWidth: 220 }}>{recipient}</TableCell>
                            <TableCell>{row.isRead ? 'Read' : 'Unread'}</TableCell>
                            <TableCell>{row.metadata?.sentBy ?? '—'}</TableCell>
                            <TableCell>{row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}</TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              )}
            </TableContainer>

            <TablePagination
              component="div"
              count={total}
              page={listPage}
              onPageChange={(e, p) => setListPage(p)}
              rowsPerPage={listLimit}
              onRowsPerPageChange={(e) => {
                setListLimit(Math.min(100, parseInt(e.target.value, 10) || 20));
                setListPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
              disabled={announcementsLoading}
            />
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default Announcements;
